import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { CurrencyAmountInput } from '@/components/ui/CurrencyAmountInput';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import {
  ApiRequestError,
  fetchServicerBankInfo,
  fetchWalletInfo,
  formatBankCardInput,
  formatWalletAmount,
  hasWalletBankInfo,
  maskBankCardNumber,
  normalizeBankCardDigits,
  postApplyWithdrawal,
  postEditBankInfo,
  showApiResultModal,
  validateBankCardNumber,
  type WalletBankInfo,
  type WalletChangeRecord,
} from '@/api';

const PAGE_SIZE = 20;

function formatRecordTime(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function recordAmountLabel(record: WalletChangeRecord): string {
  const abs = Math.abs(record.change);
  const formatted = `$${formatWalletAmount(abs)}`;
  if (record.changeType === 1) return `+${formatted}`;
  if (record.changeType === 2) return `-${formatted}`;
  return record.change >= 0 ? `+${formatted}` : `-${formatted}`;
}

function recordAmountClass(record: WalletChangeRecord): string {
  if (record.changeType === 1 || record.change > 0) return 'text-green-600';
  if (record.changeType === 2 || record.change < 0) return 'text-red-500';
  return 'text-gray-700';
}

function recordIcon(record: WalletChangeRecord): { icon: string; bg: string; color: string } {
  if (record.changeType === 1) {
    return { icon: 'ri-arrow-down-circle-line', bg: 'bg-green-50', color: 'text-green-600' };
  }
  if (record.changeType === 2) {
    return { icon: 'ri-arrow-up-circle-line', bg: 'bg-red-50', color: 'text-red-500' };
  }
  return { icon: 'ri-exchange-line', bg: 'bg-gray-100', color: 'text-gray-500' };
}

/** 限制提现金额为最多两位小数，且不超过当前余额 */
function sanitizeWithdrawalAmountInput(raw: string, maxBalance: number): string {
  let cleaned = raw.replace(/[^\d.]/g, '');

  const dotIndex = cleaned.indexOf('.');
  if (dotIndex !== -1) {
    cleaned =
      cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '');
    const [intPart, decPart = ''] = cleaned.split('.');
    cleaned = `${intPart}.${decPart.slice(0, 2)}`;
  }

  if (cleaned === '' || cleaned === '.') return cleaned;

  const numeric = Number(cleaned);
  if (Number.isNaN(numeric)) return cleaned;

  if (numeric > maxBalance) {
    return formatWalletAmount(maxBalance);
  }

  return cleaned;
}

export default function MyWalletPage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [bankInfo, setBankInfo] = useState<WalletBankInfo | undefined>();
  const [records, setRecords] = useState<WalletChangeRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const infiniteScrollEnabledRef = useRef(false);

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyAmount, setApplyAmount] = useState('');
  const [applyBankForm, setApplyBankForm] = useState({
    bankAccountName: '',
    bankCardNumber: '',
    bankName: '',
  });
  const [applyBankLoading, setApplyBankLoading] = useState(false);
  const [applyBankCardError, setApplyBankCardError] = useState('');
  const [applyError, setApplyError] = useState('');
  const [applying, setApplying] = useState(false);

  const [showBankModal, setShowBankModal] = useState(false);
  const [bankForm, setBankForm] = useState({
    bankAccountName: '',
    bankCardNumber: '',
    bankName: '',
  });
  const [bankFormError, setBankFormError] = useState('');
  const [bankCardNumberError, setBankCardNumberError] = useState('');
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSaving, setBankSaving] = useState(false);

  const hasBankInfo = hasWalletBankInfo(bankInfo);

  const hasMore = page * pageSize < totalRecords;
  const canApplySalary = balance > 0;

  const loadWallet = useCallback(async (nextPage: number, append: boolean) => {
    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      infiniteScrollEnabledRef.current = false;
      setLoading(true);
      setError('');
    }

    try {
      const result = await fetchWalletInfo({ page: nextPage, pageSize: PAGE_SIZE });
      setBalance(result.balance);
      setBankInfo(result.bankInfo);
      setTotalRecords(result.totalRecords);
      setPage(result.page);
      pageRef.current = result.page;
      setPageSize(result.pageSize);
      setRecords((prev) => {
        if (!append) return result.changeRecords;
        const seen = new Set(prev.map((item) => item.id));
        const next = [...prev];
        for (const item of result.changeRecords) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            next.push(item);
          }
        }
        return next;
      });
    } catch (err) {
      if (!append) {
        setRecords([]);
        setTotalRecords(0);
        setBalance(0);
        setBankInfo(undefined);
        setPage(1);
        pageRef.current = 1;
      }
      setError(err instanceof ApiRequestError ? err.message : 'Failed to load wallet info.');
    } finally {
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        setLoading(false);
        window.setTimeout(() => {
          infiniteScrollEnabledRef.current = true;
        }, 200);
      }
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!infiniteScrollEnabledRef.current) return;
    if (loadingMoreRef.current || loading || loadingMore) return;
    if (totalRecords === 0) return;
    if (pageRef.current * pageSize >= totalRecords) return;

    const nextPage = pageRef.current + 1;
    await loadWallet(nextPage, true);
  }, [loadWallet, loading, loadingMore, pageSize, totalRecords]);

  useEffect(() => {
    void loadWallet(1, false);
  }, [loadWallet]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || records.length === 0 || page * pageSize >= totalRecords) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!infiniteScrollEnabledRef.current || !entries[0]?.isIntersecting) return;
        void loadMore();
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore, loading, records.length, page, pageSize, totalRecords]);

  const openApplyModal = async () => {
    if (!canApplySalary) return;

    setApplyAmount(formatWalletAmount(balance));
    setApplyError('');
    setApplyBankCardError('');
    setShowApplyModal(true);
    setApplyBankLoading(true);

    try {
      const info = await fetchServicerBankInfo();
      setApplyBankForm({
        bankAccountName: info?.bankAccountName ?? bankInfo?.bankAccountName ?? '',
        bankCardNumber: formatBankCardInput(info?.bankCardNumber ?? bankInfo?.bankCardNumber ?? ''),
        bankName: info?.bankName ?? bankInfo?.bankName ?? '',
      });
    } catch {
      setApplyBankForm({
        bankAccountName: bankInfo?.bankAccountName ?? '',
        bankCardNumber: formatBankCardInput(bankInfo?.bankCardNumber ?? ''),
        bankName: bankInfo?.bankName ?? '',
      });
    } finally {
      setApplyBankLoading(false);
    }
  };

  const openBankModal = useCallback(async () => {
    setBankFormError('');
    setBankCardNumberError('');
    setShowBankModal(true);
    setBankLoading(true);
    try {
      const info = await fetchServicerBankInfo();
      setBankForm({
        bankAccountName: info?.bankAccountName ?? bankInfo?.bankAccountName ?? '',
        bankCardNumber: formatBankCardInput(info?.bankCardNumber ?? bankInfo?.bankCardNumber ?? ''),
        bankName: info?.bankName ?? bankInfo?.bankName ?? '',
      });
    } catch (err) {
      setBankForm({
        bankAccountName: bankInfo?.bankAccountName ?? '',
        bankCardNumber: formatBankCardInput(bankInfo?.bankCardNumber ?? ''),
        bankName: bankInfo?.bankName ?? '',
      });
      setBankFormError(
        err instanceof ApiRequestError ? err.message : 'Failed to load bank information.',
      );
    } finally {
      setBankLoading(false);
    }
  }, [bankInfo]);

  const handleSaveBankInfo = async () => {
    const bankAccountName = bankForm.bankAccountName.trim();
    const bankCardNumber = normalizeBankCardDigits(bankForm.bankCardNumber);
    const bankName = bankForm.bankName.trim();

    const cardError = validateBankCardNumber(bankForm.bankCardNumber);
    setBankCardNumberError(cardError ?? '');
    if (cardError) {
      setBankFormError(cardError);
      return;
    }

    if (!bankAccountName || !bankName) {
      setBankFormError('Please enter account name and bank name.');
      return;
    }

    setBankSaving(true);
    setBankFormError('');
    setBankCardNumberError('');
    try {
      await postEditBankInfo({
        bankAccountName,
        bankCardNumber,
        bankName,
      });
      setBankInfo({ bankAccountName, bankCardNumber, bankName });
      setShowBankModal(false);
      showApiResultModal({ message: 'Bank information saved successfully.', success: true });
    } catch (err) {
      setBankFormError(
        err instanceof ApiRequestError ? err.message : 'Failed to save bank information.',
      );
    } finally {
      setBankSaving(false);
    }
  };

  const handleApplySalary = async () => {
    if (!canApplySalary || applying) return;

    const amountRaw = applyAmount.trim();
    const amount = Number(amountRaw);
    const bankAccountName = applyBankForm.bankAccountName.trim();
    const bankName = applyBankForm.bankName.trim();
    const bankCardNumber = normalizeBankCardDigits(applyBankForm.bankCardNumber);
    const cardError = validateBankCardNumber(applyBankForm.bankCardNumber);

    setApplyBankCardError(cardError ?? '');

    if (!amountRaw || Number.isNaN(amount) || amount <= 0) {
      setApplyError('Please enter a valid withdrawal amount.');
      return;
    }
    if (amount > balance) {
      setApplyError(`Amount cannot exceed your current balance of $${formatWalletAmount(balance)}.`);
      return;
    }
    if (!bankAccountName || !bankName) {
      setApplyError('Please enter account holder name and bank name.');
      return;
    }
    if (cardError) {
      setApplyError(cardError);
      return;
    }

    setApplying(true);
    setApplyError('');
    try {
      const result = await postApplyWithdrawal({
        amount,
        bankName,
        bankAccountName,
        bankCardNumber,
      });

      if (result.error) {
        setApplyError(result.message);
        showApiResultModal({ message: result.message, success: false });
        return;
      }

      setShowApplyModal(false);
      showApiResultModal({ message: result.message, success: true });
      await loadWallet(1, false);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col pb-6">
      <SubpageHeader
        title="My Wallet"
        subtitle="Balance and earnings history"
        onBack={() => navigate('/me')}
      />

      <div className="px-4 pt-4 space-y-3">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-green-600 via-green-500 to-teal-500 p-3 text-white shadow-sm">
          <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-white/10 -translate-y-6 translate-x-6" />
          <div className="absolute bottom-0 left-8 w-14 h-14 rounded-full bg-white/10 translate-y-5" />
          <div className="relative">
            <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-widest">
              <i className="ri-wallet-3-line text-sm" />
              Current Balance
            </div>
            {loading ? (
              <div className="h-10 w-36 bg-white/20 rounded-lg animate-pulse mt-3" />
            ) : (
              <div className="text-4xl font-bold tracking-tight mt-2">
                ${formatWalletAmount(balance)}
              </div>
            )}

            {loading ? (
              <div className="h-10 bg-white/10 rounded-lg animate-pulse mt-3" />
            ) : hasBankInfo ? (
              <div className="mt-3 rounded-lg bg-white/10 px-3 py-2.5 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-white/80 text-[11px] font-semibold uppercase tracking-wide min-w-0">
                    <i className="ri-bank-card-line text-sm flex-shrink-0" />
                    <span>Payout Bank Account</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void openBankModal()}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/15 hover:bg-white/25 text-white cursor-pointer transition-colors flex-shrink-0"
                    aria-label="Update bank details"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                </div>
                <div className="text-white text-sm font-semibold tracking-wide">
                  {maskBankCardNumber(bankInfo?.bankCardNumber ?? '')}
                </div>
                <div className="text-white/75 text-xs">
                  {[bankInfo?.bankName, bankInfo?.bankAccountName].filter(Boolean).join(' · ')}
                </div>
              </div>
            ) : (
              <div className="mt-3 rounded-lg bg-amber-400/20 border border-amber-200/30 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <i className="ri-error-warning-line text-amber-100 text-base mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white text-xs font-semibold leading-relaxed">
                      Bank account not set up
                    </p>
                    <p className="text-white/80 text-[11px] mt-1 leading-relaxed">
                      Add your bank details to receive salary payouts.
                    </p>
                    <button
                      type="button"
                      onClick={() => void openBankModal()}
                      className="mt-2 text-[11px] font-semibold text-white bg-white/20 hover:bg-white/30 px-2.5 py-1 rounded-lg cursor-pointer transition-colors"
                    >
                      Set Up Bank Account
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-white/75 text-xs mt-2">
              Submit a withdrawal request to transfer funds to your bank account.
            </p>
            <button
              type="button"
              onClick={() => void openApplyModal()}
              disabled={!canApplySalary || loading}
              className={`mt-4 w-full h-9 rounded-lg font-medium text-xs transition-colors flex items-center justify-center gap-1.5 ${
                canApplySalary && !loading
                  ? 'bg-white text-green-700 hover:bg-green-50 cursor-pointer'
                  : 'bg-white/30 text-white/70 cursor-not-allowed'
              }`}
            >
              <i className="ri-money-dollar-circle-line text-sm" />
              Apply for Salary
            </button>
            {!loading && balance <= 0 ? (
              <p className="text-white/75 text-[11px] mt-2 text-center leading-relaxed">
                No balance available for withdrawal.
              </p>
            ) : null}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-green-50">
                <i className="ri-history-line text-green-600 text-xs" />
              </div>
              <span className="text-gray-800 font-semibold text-sm">Wallet Records</span>
            </div>
            {!loading ? (
              <span className="text-[10px] text-gray-400 font-medium">{totalRecords} total</span>
            ) : null}
          </div>

          {loading ? (
            <div className="px-4 py-10 text-center text-gray-400 text-sm">
              <i className="ri-loader-4-line text-2xl animate-spin block mx-auto mb-2 text-green-500" />
              Loading wallet records…
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center">
              <p className="text-red-500 text-sm mb-3" role="alert">{error}</p>
              <button
                type="button"
                onClick={() => void loadWallet(1, false)}
                className="text-green-600 text-sm font-semibold cursor-pointer"
              >
                Try again
              </button>
            </div>
          ) : records.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <i className="ri-file-list-3-line text-3xl text-gray-300 block mb-2" />
              <p className="text-gray-400 text-sm">No wallet records yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {records.map((record) => {
                const visual = recordIcon(record);
                return (
                  <div key={record.id} className="px-4 py-2.5 flex items-start gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${visual.bg}`}>
                      <i className={`${visual.icon} ${visual.color} text-base`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-gray-800 text-sm font-semibold">
                            {record.changeTypeText}
                          </div>
                          <div className="text-gray-400 text-[11px] mt-0.5">
                            {formatRecordTime(record.createDate)}
                          </div>
                        </div>
                        <div className={`text-sm font-bold whitespace-nowrap ${recordAmountClass(record)}`}>
                          {recordAmountLabel(record)}
                        </div>
                      </div>
                      {record.description ? (
                        <p className="text-gray-500 text-xs mt-1.5 leading-relaxed break-words">
                          {record.description}
                        </p>
                      ) : null}
                      <div className="text-gray-400 text-[10px] mt-1">
                        Balance after: ${formatWalletAmount(record.balance)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
              {loadingMore ? (
                <div className="text-center py-3 text-gray-400 text-xs font-medium flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line text-base animate-spin" />
                  Loading more…
                </div>
              ) : null}
              {!loadingMore && !hasMore ? (
                <div className="text-center py-2 text-gray-300 text-[11px]">All records loaded</div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {showBankModal ? (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => !bankSaving && setShowBankModal(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[88vh] overflow-y-auto"
            style={{ maxWidth: 480, margin: '0 auto' }}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <div className="mb-5">
              <h2 className="text-gray-900 font-bold text-lg">Bank Account</h2>
              <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                Enter your payout bank details. Your card number will be masked on this page.
              </p>
            </div>

            {bankLoading ? (
              <div className="py-10 text-center text-gray-400 text-sm">
                <i className="ri-loader-4-line text-2xl animate-spin block mx-auto mb-2 text-green-500" />
                Loading bank information…
              </div>
            ) : (
              <div className="space-y-3">
                <BankField
                  label="Account Holder Name"
                  value={bankForm.bankAccountName}
                  onChange={(value) => setBankForm((prev) => ({ ...prev, bankAccountName: value }))}
                  placeholder="Name on bank account"
                />
                <BankField
                  label="Bank Name"
                  value={bankForm.bankName}
                  onChange={(value) => setBankForm((prev) => ({ ...prev, bankName: value }))}
                  placeholder="e.g. Commonwealth Bank"
                />
                <BankField
                  label="Card Number"
                  value={bankForm.bankCardNumber}
                  onChange={(value) => {
                    setBankForm((prev) => ({ ...prev, bankCardNumber: formatBankCardInput(value) }));
                    if (bankCardNumberError) {
                      setBankCardNumberError(validateBankCardNumber(value) ?? '');
                    }
                  }}
                  onBlur={() => {
                    setBankCardNumberError(validateBankCardNumber(bankForm.bankCardNumber) ?? '');
                  }}
                  placeholder="1234 5678 9012 3456"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  error={bankCardNumberError}
                  maxLength={23}
                />
                <InlineFieldError message={bankFormError} />
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowBankModal(false)}
                disabled={bankSaving}
                className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 font-medium text-xs cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveBankInfo()}
                disabled={bankSaving || bankLoading}
                className="flex-1 h-9 rounded-lg bg-green-500 text-white font-medium text-xs cursor-pointer hover:bg-green-600 transition-colors disabled:opacity-60"
              >
                {bankSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {showApplyModal ? (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => !applying && setShowApplyModal(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[88vh] overflow-y-auto"
            style={{ maxWidth: 480, margin: '0 auto' }}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <div className="mb-5">
              <h2 className="text-gray-900 font-bold text-lg">Apply for Salary</h2>
              <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                Submit a withdrawal request. Available balance:{' '}
                <span className="font-semibold text-green-600">${formatWalletAmount(balance)}</span>
              </p>
            </div>

            {applyBankLoading ? (
              <div className="py-10 text-center text-gray-400 text-sm">
                <i className="ri-loader-4-line text-2xl animate-spin block mx-auto mb-2 text-green-500" />
                Loading bank information…
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label htmlFor="withdrawal-amount" className="block text-xs font-normal text-gray-600 mb-1.5">
                    Withdrawal Amount
                  </label>
                  <CurrencyAmountInput
                    id="withdrawal-amount"
                    type="text"
                    inputMode="decimal"
                    value={applyAmount}
                    onChange={(value) => {
                      setApplyAmount(sanitizeWithdrawalAmountInput(value, balance));
                      if (applyError) setApplyError('');
                    }}
                    currencySymbol="$"
                    placeholder="0.00"
                  />
                  <p className="text-gray-400 text-[10px] mt-1">
                    Maximum: ${formatWalletAmount(balance)}
                  </p>
                </div>

                <BankField
                  label="Account Holder Name"
                  value={applyBankForm.bankAccountName}
                  onChange={(value) => setApplyBankForm((prev) => ({ ...prev, bankAccountName: value }))}
                  placeholder="Name on bank account"
                />
                <BankField
                  label="Bank Name"
                  value={applyBankForm.bankName}
                  onChange={(value) => setApplyBankForm((prev) => ({ ...prev, bankName: value }))}
                  placeholder="e.g. Commonwealth Bank"
                />
                <BankField
                  label="Card Number"
                  value={applyBankForm.bankCardNumber}
                  onChange={(value) => {
                    setApplyBankForm((prev) => ({ ...prev, bankCardNumber: formatBankCardInput(value) }));
                    if (applyBankCardError) {
                      setApplyBankCardError(validateBankCardNumber(value) ?? '');
                    }
                  }}
                  onBlur={() => {
                    setApplyBankCardError(validateBankCardNumber(applyBankForm.bankCardNumber) ?? '');
                  }}
                  placeholder="1234 5678 9012 3456"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  error={applyBankCardError}
                  maxLength={23}
                />
                <InlineFieldError message={applyError} />
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowApplyModal(false)}
                disabled={applying}
                className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 font-medium text-xs cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleApplySalary()}
                disabled={applying || applyBankLoading}
                className="flex-1 h-9 rounded-lg bg-green-500 text-white font-medium text-xs cursor-pointer hover:bg-green-600 transition-colors disabled:opacity-60"
              >
                {applying ? 'Submitting…' : 'Submit Application'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function BankField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  inputMode,
  autoComplete,
  error,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
  autoComplete?: string;
  error?: string;
  maxLength?: number;
}) {
  return (
    <div>
      <label className="block text-xs font-normal text-gray-600 mb-1.5">{label}</label>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={maxLength}
        aria-invalid={Boolean(error)}
        className={`w-full h-8 px-2.5 text-xs font-normal border bg-gray-50 rounded-md outline-none placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
          error
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-200 focus:border-green-500'
        }`}
      />
      {error ? <p className="text-red-500 text-[11px] mt-1">{error}</p> : null}
    </div>
  );
}
